import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, FormField, useToast } from '@vd/ui';
import { useAuth } from '../auth/AuthProvider';
import { api, type WorkspaceMarkAnswer } from '../../lib/api';
import { ProjectColorPicker } from '../projects/ProjectColorPicker';
import { WorkspaceMark } from './WorkspaceMark';

/**
 * A workspace's colour and logo in its settings (TOADO-906). Each saves at
 * once. The logo is uploaded and Toado keeps its own copy; there is no field
 * for an address.
 */
export function WorkspaceMarkField({ company, canEdit }: {
  company: { id: string; name: string } & WorkspaceMarkAnswer;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'color' | 'upload' | 'remove' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const settle = async (answer: Partial<WorkspaceMarkAnswer>) => {
    queryClient.setQueryData(['company', company.id], (current: unknown) => (current ? { ...(current as object), ...answer } : current));
    // The switcher reads the workspace list from /me. A refresh that fails
    // leaves the switcher behind until the next one, not the save undone
    // (z.ai, round 2).
    await refreshUser().catch(() => undefined);
  };
  const run = async (kind: 'color' | 'upload' | 'remove', act: () => Promise<Partial<WorkspaceMarkAnswer>>, done: string) => {
    setBusy(kind);
    setError(null);
    try {
      await settle(await act());
      toast.push({ variant: 'success', title: done });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // An API from before TOADO-906 names no colour and has no logo routes:
  // nothing here would save, so nothing is offered.
  if (typeof company.color !== 'string') return null;

  return (
    <div style={{ display: 'grid', gap: 12 }} data-testid="workspace-mark-field">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <WorkspaceMark company={company} size={40} />
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Shown in the workspace switcher{company.hasUploadedLogo ? '.' : ', with the initials until a logo is uploaded.'}
        </span>
      </div>
      <FormField label="Color">
        <ProjectColorPicker
          label="Workspace color"
          paletteOnly
          disabled={!canEdit || busy !== null}
          value={company.color}
          onChange={(color) => void run('color', async () => {
            const saved = await api.updateCompany(company.id, { color });
            return { color: saved.color };
          }, 'Color saved')}
        />
      </FormField>
      <FormField label="Logo" help="A PNG or JPEG of up to 2 MB. Toado keeps its own copy at 64 pixels.">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void run('upload', () => api.uploadWorkspaceLogo(company.id, file), 'Logo saved');
            }}
          />
          <Button variant="secondary" size="sm" disabled={!canEdit || busy !== null} loading={busy === 'upload'} onClick={() => input.current?.click()}>
            {company.hasUploadedLogo ? 'Replace logo' : 'Upload a logo'}
          </Button>
          {company.hasUploadedLogo && (
            <Button variant="ghost" size="sm" disabled={!canEdit || busy !== null} loading={busy === 'remove'} onClick={() => void run('remove', () => api.removeWorkspaceLogo(company.id), 'Logo removed')}>
              Remove logo
            </Button>
          )}
        </div>
      </FormField>
      {error && <p role="alert" style={{ color: 'var(--danger)', fontSize: 12, margin: 0 }}>{error}</p>}
    </div>
  );
}
